import type { PipControlState, PipOpenOptions, PipPresence, PipTracks } from './interfaces'

import {
  ICON_CAM_OFF,
  ICON_CAM_ON,
  ICON_MIC_OFF,
  ICON_MIC_ON,
  ICON_USER,
  buildPipStyles,
} from './pip-assets'
import { readPipPalette } from './pip-colors'

export interface PipViewElements {
  videos: { tutor: HTMLVideoElement, student: HTMLVideoElement }
  tiles: { tutor: HTMLElement, student: HTMLElement }
  buttons: { mic: HTMLButtonElement, cam: HTMLButtonElement }
}

interface PipTile {
  tile: HTMLElement
  video: HTMLVideoElement
}

// Tracks the last applied MediaStreamTrack per video to avoid re-attach flicker
const appliedTracks = new WeakMap<HTMLVideoElement, MediaStreamTrack | null>()

const createTile = (doc: Document, label?: string): PipTile => {
  const tile = doc.createElement('div')
  tile.className = 'pip__tile pip__tile--empty'

  const video = doc.createElement('video')
  video.className = 'pip__video'
  video.autoplay = true
  video.muted = true
  video.playsInline = true

  const placeholder = doc.createElement('div')
  placeholder.className = 'pip__placeholder'
  placeholder.innerHTML = ICON_USER

  tile.append(video, placeholder)

  if (label) {
    const labelEl = doc.createElement('span')
    labelEl.className = 'pip__label'
    labelEl.textContent = label
    tile.append(labelEl)
  }

  return { tile, video }
}

const createControl = (doc: Document, ariaLabel: string, onClick: () => void): HTMLButtonElement => {
  const button = doc.createElement('button')
  button.className = 'pip__control'
  button.type = 'button'
  button.setAttribute('aria-label', ariaLabel)
  button.addEventListener('click', onClick)

  return button
}

export const buildPipView = (doc: Document, options: PipOpenOptions): PipViewElements => {
  const style = doc.createElement('style')
  style.textContent = buildPipStyles(readPipPalette())
  doc.head.append(style)

  const root = doc.createElement('div')
  root.className = 'pip'

  const tiles = doc.createElement('div')
  tiles.className = 'pip__tiles'

  const tutor = createTile(doc, options.labels?.tutor)
  const student = createTile(doc, options.labels?.student)
  tiles.append(tutor.tile, student.tile)

  const controls = doc.createElement('div')
  controls.className = 'pip__controls'

  const mic = createControl(doc, 'Toggle microphone', options.handlers.onToggleMic)
  const cam = createControl(doc, 'Toggle camera', options.handlers.onToggleCam)
  controls.append(mic, cam)

  root.append(tiles, controls)
  doc.body.append(root)

  return {
    videos: { tutor: tutor.video, student: student.video },
    tiles: { tutor: tutor.tile, student: student.tile },
    buttons: { mic, cam },
  }
}

const applyTrackToTile = (tile: HTMLElement, video: HTMLVideoElement, track: MediaStreamTrack | null): void => {
  if (appliedTracks.get(video) === track) return

  appliedTracks.set(video, track)
  video.srcObject = track ? new MediaStream([track]) : null
  tile.classList.toggle('pip__tile--empty', !track)
}

export const applyTracks = (view: PipViewElements, tracks: PipTracks): void => {
  applyTrackToTile(view.tiles.tutor, view.videos.tutor, tracks.tutor)
  applyTrackToTile(view.tiles.student, view.videos.student, tracks.student)
}

export const applyPresence = (view: PipViewElements, presence: PipPresence): void => {
  view.tiles.tutor.classList.toggle('pip__tile--absent', !presence.tutor)
  view.tiles.student.classList.toggle('pip__tile--absent', !presence.student)
}

const applyControlState = (button: HTMLButtonElement, enabled: boolean, iconOn: string, iconOff: string): void => {
  button.innerHTML = enabled ? iconOn : iconOff
  button.classList.toggle('pip__control--off', !enabled)
  button.setAttribute('aria-pressed', String(enabled))
}

export const applyState = (view: PipViewElements, state: PipControlState): void => {
  applyControlState(view.buttons.mic, state.micEnabled, ICON_MIC_ON, ICON_MIC_OFF)
  applyControlState(view.buttons.cam, state.camEnabled, ICON_CAM_ON, ICON_CAM_OFF)
}
